import type Luminary from '@lumen5/luminary';

import { captureVideoStateContext, type CaptureVideoStateContextResult } from './context';
import type { VideoStateSnapshot } from './types';

import type { SceneAddressing } from 'js/src/agent/tools/LintVideoTool';
import type { MediaCandidates } from 'js/src/client';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';

/**
 * Manages video state snapshots and diff generation for agents.
 * Encapsulates all video state tracking logic in a reusable, testable class.
 *
 * Responsibilities:
 * - Store and manage video state snapshots
 * - Generate diffs between current and previous state
 * - Initialize video context for conversations
 */
export class VideoStateManager {
    private lastSnapshot: VideoStateSnapshot | null = null;
    private luminary: Luminary;
    private getState: GetRootState;
    private sceneAddressing: SceneAddressing;

    constructor(
        luminary: Luminary,
        getState: GetRootState,
        sceneAddressing: SceneAddressing = 'index',
    ) {
        this.luminary = luminary;
        this.getState = getState;
        this.sceneAddressing = sceneAddressing;
    }

    /** Addressing mode for captured state: 'scene_id' (Toolbox) or 'index' (legacy agents). */
    public getSceneAddressing(): SceneAddressing {
        return this.sceneAddressing;
    }

    /**
     * The Redux accessor this manager captures snapshots with. Redux state is required in the
     * editing pipeline (coverage tells overlay from base scenes, and pauses live in the Lexical
     * editor), so this is never absent — the only storeless capture path is the reference-video
     * describer, which bypasses this manager entirely. This manager is the single source of
     * coverage/pause fidelity: any caller that captures a snapshot destined for this manager's
     * store (e.g. an edit tool computing its before/after diff, or create_blank_video seeding the
     * store) must capture with THIS accessor, so every stored snapshot agrees on whether coverage
     * and pauses are present. Mixing a with-coverage snapshot and a without-coverage one in the
     * same store produces phantom "coverage/pause added/removed" diffs on every voicedriven scene.
     */
    public getStateAccessor(): GetRootState {
        return this.getState;
    }

    public getLastSnapshot(): VideoStateSnapshot | null {
        return this.lastSnapshot;
    }

    public setSnapshot(snapshot: VideoStateSnapshot): void {
        this.lastSnapshot = snapshot;
    }

    public clearSnapshot(): void {
        this.lastSnapshot = null;
    }

    /**
     * Repoints the manager at a different luminary (e.g. when the user selects
     * a different video draft in Sketch) and clears the snapshot so the next
     * state read is a full snapshot of the new video rather than a diff
     * against the previous one.
     */
    public setLuminary(luminary: Luminary): void {
        this.luminary = luminary;
        this.lastSnapshot = null;
    }

    public async getStateDiff(options?: {
        mediaCandidates?: MediaCandidates | null;
        useDiffMode?: boolean;
    }): Promise<CaptureVideoStateContextResult> {
        const result = await captureVideoStateContext(this.luminary, {
            mediaCandidates: options?.mediaCandidates,
            previousState: this.lastSnapshot,
            useDiffMode: options?.useDiffMode ?? true,
            getState: this.getState,
            sceneAddressing: this.sceneAddressing,
        });

        return result;
    }

    public async initializeContext(options?: {
        mediaCandidates?: MediaCandidates | null;
    }): Promise<string | null> {
        const result = await captureVideoStateContext(this.luminary, {
            mediaCandidates: options?.mediaCandidates,
            previousState: this.lastSnapshot,
            useDiffMode: true,
            getState: this.getState,
            sceneAddressing: this.sceneAddressing,
        });

        // Store the snapshot for future diffs
        this.setSnapshot(result.snapshot);

        return result.description;
    }
}
